//@ts-check
import { renderAllSections } from './js-utils-rendersection.js'

/**
 * Builds a cart/update.js `updates` map that removes a line and, when it is a
 * parent, every option linked to that same add group.
 *
 * Prefers `_option_group` (unique per add). Falls back to `_parent_id` /
 * variant id only when a single parent line uses that variant, to avoid
 * deleting options that belong to another parent with the same variant.
 *
 * @param {Array<{ key: string, variant_id: number, properties: Record<string, string> | null }>} items
 * @param {string} lineKey
 * @returns {Record<string, number>}
 */
function buildUpdatesRemovingLineAndChildren(items, lineKey) {
  /** @type {Record<string, number>} */
  const updates = { [lineKey]: 0 }

  const removedItem = items.find((item) => item.key === lineKey)
  if (!removedItem) return updates

  const parentIdProperty = removedItem.properties?._parent_id
  const isChild = parentIdProperty != null && String(parentIdProperty) !== ''

  // Only cascade when removing a parent (not when removing a single option).
  if (isChild) return updates

  const optionGroup = removedItem.properties?._option_group
  if (optionGroup != null && String(optionGroup) !== '') {
    const group = String(optionGroup)
    for (const item of items) {
      if (String(item.properties?._option_group ?? '') === group) {
        updates[item.key] = 0
      }
    }
    return updates
  }

  const parentVariantId = String(removedItem.variant_id)
  const parentLinesForVariant = items.filter((item) => {
    const itemIsChild =
      item.properties?._parent_id != null &&
      String(item.properties._parent_id) !== ''
    return !itemIsChild && String(item.variant_id) === parentVariantId
  })

  // Same variant can be multiple parent lines (different properties). Avoid
  // cascading by variant id when more than one parent shares it.
  if (parentLinesForVariant.length > 1) {
    return updates
  }

  for (const item of items) {
    const childParentId = item.properties?._parent_id
    if (childParentId != null && String(childParentId) === parentVariantId) {
      updates[item.key] = 0
    }
  }

  return updates
}

class CDeleteProduct extends HTMLElement {
  constructor() {
    super()
    this.productId = this.getAttribute('product-id')
    this.btn = this.querySelector('button')
    this.sectionsRender = [
      {
        title: 'header',
        selector: ['[data-render-icon-cart]'],
      },
      {
        title: 'cart-drawer',
        selector: ['[data-render-total]', '[data-render-panel-cart]'],
      },
      {
        title: 'product',
        selector: [
          '[data-render-btn-add-product]',
          '[data-render-panel-contact]',
          '[data-render-panel-product]',
        ],
      },
    ]
  }

  connectedCallback() {
    this.btn?.addEventListener('click', (e) => {
      void this.handleUpdateCart(e)
    })
  }

  /**
   * @param {Event} e
   */
  async handleUpdateCart(e) {
    e.preventDefault()
    if (!this.productId) return

    try {
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`)
      const cart = await cartResponse.json()
      const items = Array.isArray(cart.items) ? cart.items : []
      const updates = buildUpdatesRemovingLineAndChildren(items, this.productId)

      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates }),
      })
    } catch (error) {
      console.error('Error:', error)
    } finally {
      renderAllSections(this.sectionsRender)
    }
  }

  disconnectedCallback() {}
}
if (!customElements.get('c-delete-product')) {
  customElements.define('c-delete-product', CDeleteProduct)
}
